<script setup lang="ts">
import { AppRawButton, AppSvgIcon } from '@libs/ui';

import type {
  ItineraryLocationLoaderSegment,
  ItineraryLocationSegment,
} from '../../../models';
import { ItinerarySegmentType } from '../../../models';
import { useTripOverviewPageStore } from '../../../stores';
import ItineraryIcon from '../ItineraryIcon.vue';

import ItineraryLocation from './ItineraryLocation.vue';

interface ItineraryLocationSegmentProps {
  segment: ItineraryLocationSegment | ItineraryLocationLoaderSegment;
  printMode: boolean;
}

const props = defineProps<ItineraryLocationSegmentProps>();

const tripOverviewPageStore = useTripOverviewPageStore();

function changeLocation(): void {
  if (!props.printMode) {
    tripOverviewPageStore.changeLocation(props.segment);
  }
}
</script>

<template>
  <div class="itinerary-location-segment">
    <AppRawButton
      class="itinerary-location-segment-grid"
      :class="{
        'simple-location':
          segment.segmentType === ItinerarySegmentType.StartLocation ||
          segment.segmentType === ItinerarySegmentType.EndLocation,
        'print-mode': props.printMode,
      }"
      @click.stop="changeLocation"
    >
      <ItineraryIcon
        class="itinerary-icon"
        :segment-type="props.segment.segmentType"
      />
      <ItineraryLocation
        :location="segment.location.name"
        :country="segment.location.country"
      />
      <AppSvgIcon
        v-if="!props.printMode"
        class="edit-icon"
        name="pencil"
        size="24"
      />
    </AppRawButton>
  </div>
</template>

<style scoped lang="scss">
.itinerary-location-segment {
  margin-bottom: var(--spacing-24);
  border: 1px solid var(--color-neutral-30);
  border-radius: var(--border-radius-8);
  background-color: var(--color-white);
  position: relative;
  @include for-less-or-equal-than-1199-px {
    margin: 0 var(--spacing-24) var(--spacing-24) var(--spacing-24);
  }

  .itinerary-location-segment-grid {
    width: -webkit-fill-available;
    border-radius: var(--border-radius-8);
    transition: var(--transition-very-short);
    padding: var(--spacing-16) var(--spacing-8);

    display: grid;
    column-gap: var(--spacing-8);
    row-gap: var(--spacing-24);
    grid-template-columns:
      var(--spacing-32) auto var(--spacing-32) min-content
      var(--spacing-32);
    grid-template-rows: var(--spacing-32);
    align-items: center;

    &.simple-location {
      grid-template-columns: var(--spacing-32) auto var(--spacing-32);
    }

    &.print-mode {
      .nights-selector-desktop {
        border-left: none;
        border-right: none;
      }
    }

    &.hide-pencil-icon {
      grid-template-columns:
        var(--spacing-32) auto min-content
        var(--spacing-32);

      .edit-icon {
        display: none;
      }
    }

    &:not(.print-mode) {
      @include for-more-or-equal-than-599-px {
        @include on-hover() {
          /* stylelint-disable-next-line selector-pseudo-class-disallowed-list */
          &:not(:has(.no-parent-hover:hover)) {
            background: var(--color-neutral-20);
            cursor: pointer;
          }
        }
      }
    }

    .nights-selector-desktop {
      all: unset;
      height: 100%;
      display: flex;
      align-items: center;
      border-left: 1px solid var(--color-neutral-30);
      border-right: 1px solid var(--color-neutral-30);
    }

    @include for-less-or-equal-than-599-px {
      grid-template-columns: var(--spacing-32) auto var(--spacing-32);
      grid-template-rows: var(--spacing-32);

      .edit-icon,
      .nights-selector-desktop {
        display: none;
      }

      .nights-selector-mobile {
        display: block;
        grid-column: 3 / auto;
      }
    }
  }

  .nights-selector-mobile {
    display: none;
  }

  @include for-less-or-equal-than-599-px {
    margin: 0 var(--spacing-12) var(--spacing-24) var(--spacing-12);

    .nights-selector-mobile {
      padding: var(--spacing-12) var(--spacing-8);
      display: flex;
      align-items: center;
      justify-content: flex-start;
      border-top: 1px solid var(--color-neutral-30);
      height: 47px;
    }
  }

  &::after {
    content: '';
    height: 25px;
    width: 0px;
    border-right: 1px dashed var(--color-neutral-70);
    position: absolute;
    bottom: -25px;
    left: 24px;
  }
}
</style>
